import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Paper,
  Stack,
  CircularProgress,
  Pagination,
  Link,
} from '@mui/material';

import DashboardLayout from '../../layout/DashboardLayout';
import { profileNlSearch, SearchApiError } from '../../services/icpSearchService';

function NlProfileRow({ doc, index }) {
  const name =
    doc.full_name ||
    [doc.firstName, doc.lastName].filter(Boolean).join(' ') ||
    doc.name ||
    `Profile ${index + 1}`;
  const sub =
    doc.headline ||
    doc.summary ||
    doc.current_title ||
    doc.title ||
    '';
  const loc =
    doc.location ||
    [doc.city, doc.region].filter(Boolean).join(', ') ||
    '';
  const li =
    doc.linkedin_url ||
    doc.linkedinUrl ||
    doc.public_identifier ||
    '';

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 1, borderRadius: 2 }}>
      <Typography variant="subtitle1" fontWeight={600}>
        {name}
      </Typography>
      {sub && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {sub}
        </Typography>
      )}
      {loc && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
          {loc}
        </Typography>
      )}
      {li && (
        <Link
          href={li.startsWith('http') ? li : `https://linkedin.com/in/${li}`}
          target="_blank"
          rel="noopener noreferrer"
          variant="caption"
        >
          LinkedIn
        </Link>
      )}
    </Paper>
  );
}

export default function IcpSearchPage() {
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const runSearch = async (nextPage = 1) => {
    setError('');
    setResult(null);
    if (!query.trim()) {
      setError('Enter a search query.');
      return;
    }
    setLoading(true);
    try {
      const body = await profileNlSearch({
        query: query.trim(),
        page: nextPage,
        limit: 20,
      });
      if (body?.status !== 'success') {
        setError(body?.message || body?.error || 'Search failed.');
        return;
      }
      setResult(body);
      setPage(nextPage);
    } catch (e) {
      if (e instanceof SearchApiError) {
        setError(e.message || 'Request failed.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const iq = result?.interpretedQuery;
  const interpretationLine =
    iq &&
    [
      iq.locations?.length ? `Locations: ${iq.locations.join(', ')}` : '',
      iq.minYearsExperience != null || iq.maxYearsExperience != null
        ? `Experience: ${iq.minYearsExperience ?? '—'}–${iq.maxYearsExperience ?? '—'} years`
        : '',
      iq.keywords?.length ? `Keywords: ${iq.keywords.join(', ')}` : '',
      iq.jobTitleHints?.length ? `Titles: ${iq.jobTitleHints.join(', ')}` : '',
      iq.parseWarning || '',
    ]
      .filter(Boolean)
      .join(' · ');

  return (
    <DashboardLayout pageName="Profile">
      <Box sx={{ m: 2, maxWidth: 900 }}>
        <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>
          Search
        </Typography>

        <Paper sx={{ p: 2, borderRadius: 2 }}>
          <TextField
            label="Query"
            placeholder="Describe who you are looking for…"
            fullWidth
            multiline
            minRows={3}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            sx={{ mb: 2 }}
          />

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <Button
            variant="contained"
            onClick={() => runSearch(1)}
            disabled={loading}
            sx={{ bgcolor: '#1BE885', color: '#0a1128', '&:hover': { bgcolor: '#17d677' }, mb: 2 }}
          >
            {loading ? <CircularProgress size={22} color="inherit" /> : 'Search'}
          </Button>

          {interpretationLine && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              We interpreted your search as: {interpretationLine}
            </Typography>
          )}

          {result?.data?.length > 0 && (
            <>
              <Stack spacing={0}>
                {result.data.map((row, i) => (
                  <NlProfileRow key={row._id || i} doc={row} index={i} />
                ))}
              </Stack>
              {result.pagination && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Pagination
                    count={result.pagination.totalPages || 1}
                    page={page}
                    onChange={(_, p) => runSearch(p)}
                    color="primary"
                    disabled={loading}
                  />
                </Box>
              )}
            </>
          )}

          {result && (!result.data || result.data.length === 0) && !loading && (
            <Typography color="text.secondary">No profiles for this query.</Typography>
          )}
        </Paper>
      </Box>
    </DashboardLayout>
  );
}
